import React, { useMemo, useEffect } from 'react';
import type { ValidationError } from '@/utils/validation/types';
import { ShortTurnConfigForm } from '@/components/config_forms/short_turn/ShortTurnConfigForm';
import type { ShortTurnConfig as ShortTurnConfigType } from '@/types';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import { getFieldErrorElement } from '@/utils/validation/helpers';
import { toast } from 'sonner';

interface ClientShortTurnConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  noCard?: boolean;
  isCustomized?: boolean;
  granularCustomizedFields?: Record<string, boolean>;
  onRevert?: () => void;
  hideTitle?: boolean;
  hideRevert?: boolean;
  hideToggle?: boolean;
}

export const ClientShortTurnConfig: React.FC<ClientShortTurnConfigProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  isLoading,
  errors = [],
  touchedFields = new Set(),
  noCard,
  isCustomized,
  granularCustomizedFields,
  onRevert,
  hideTitle,
  hideRevert,
  hideToggle,
}) => {
  const { uses_short_turn, rate_multipliers = [], roles = [], minimum_billable_hours } = formData;
  
  const shortTurnConfig: ShortTurnConfigType = {
    requiredBreakHours: formData.required_break_hours ?? null,
    floorTierCode: formData.short_turn_floor_tier_code ?? null,
    floorTierCustomValue: formData.short_turn_floor_tier_custom_value ?? null,
    floorSource: (formData.short_turn_floor_source as ShortTurnConfigType['floorSource']) ?? 'none',
    overtimeOffset: formData.short_turn_overtime_offset ?? 1,
    surchargeType: (formData.short_turn_surcharge_type as ShortTurnConfigType['surchargeType']) || 'carry_over',
    shortTurnFixedSurchargeAmount: formData.short_turn_fixed_surcharge_amount ?? null,
    surchargeAppliesToShortedHoursOnly: formData.surcharge_applies_to_shorted_hours_only ?? false,
    premiumBasis: (formData.short_turn_premium_basis as ShortTurnConfigType['premiumBasis']) ?? 'base_rate',
    premiumType: formData.short_turn_premium_type as ShortTurnConfigType['premiumType'],
    premiumValue: formData.short_turn_premium_value ?? null,
    applicableRoleIds: formData.short_turn_applicable_role_ids ?? null,
    applyOnHalfDays: formData.short_turn_apply_on_half_days ?? true,
  };
  
  const hasAdditionalMultipliers = rate_multipliers.some(m => m.code !== 'STRAIGHT_TIME');

  const shortTurnConfigCustomized: Record<string, boolean> = {
    requiredBreakHours: granularCustomizedFields?.required_break_hours ?? false,
    floorTierCode: granularCustomizedFields?.short_turn_floor_tier_code ?? false,
    floorTierCustomValue: granularCustomizedFields?.short_turn_floor_tier_custom_value ?? false,
    floorSource: granularCustomizedFields?.short_turn_floor_source ?? false,
    overtimeOffset: granularCustomizedFields?.short_turn_overtime_offset ?? false,
    surchargeType: granularCustomizedFields?.short_turn_surcharge_type ?? false,
    shortTurnFixedSurchargeAmount: granularCustomizedFields?.short_turn_fixed_surcharge_amount ?? false,
    surchargeAppliesToShortedHoursOnly: granularCustomizedFields?.surcharge_applies_to_shorted_hours_only ?? false,
    premiumBasis: granularCustomizedFields?.short_turn_premium_basis ?? false,
    premiumType: granularCustomizedFields?.short_turn_premium_type ?? false,
    premiumValue: granularCustomizedFields?.short_turn_premium_value ?? false,
    applyOnHalfDays: granularCustomizedFields?.short_turn_apply_on_half_days ?? false,
    applicableRoleIds: granularCustomizedFields?.short_turn_applicable_role_ids ?? false,
  };

  const hasMultipliersWithThresholds = useMemo(() => {
    return (rate_multipliers ?? []).some(m =>
      m.code !== 'STRAIGHT_TIME' &&
      (m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null)
    );
  }, [rate_multipliers]);

  useEffect(() => {
    if (uses_short_turn &&
        (shortTurnConfig.surchargeType === 'carry_over' || shortTurnConfig.surchargeType === 'bump_up') &&
        !hasMultipliersWithThresholds) {

      const newSurchargeType = hasAdditionalMultipliers ? 'offset_overtime' : 'fixed_dollar';
      
      handleChange('short_turn_surcharge_type', newSurchargeType);
      
      toast.info("Short turn surcharge method updated for this client.", {
        description: "The previous method is unavailable because no multipliers with thresholds are defined.",
      });
    }
  }, [uses_short_turn, shortTurnConfig.surchargeType, hasMultipliersWithThresholds, hasAdditionalMultipliers, handleChange]);

  const onShortTurnConfigChange = (updates: Partial<ShortTurnConfigType>) => {
    if (updates.surchargeType === 'hourly_premium' && !formData.short_turn_premium_type) {
      handleChange('short_turn_premium_type', 'fixed');
    }
    if (updates.requiredBreakHours !== undefined) handleChange('required_break_hours', updates.requiredBreakHours);
    if (updates.floorTierCode !== undefined) handleChange('short_turn_floor_tier_code', updates.floorTierCode);
    if (updates.floorTierCustomValue !== undefined) handleChange('short_turn_floor_tier_custom_value', updates.floorTierCustomValue);
    if (updates.floorSource !== undefined) handleChange('short_turn_floor_source', updates.floorSource);
    if (updates.overtimeOffset !== undefined) handleChange('short_turn_overtime_offset', updates.overtimeOffset);
    if (updates.surchargeType !== undefined) handleChange('short_turn_surcharge_type', updates.surchargeType);
    if (updates.shortTurnFixedSurchargeAmount !== undefined) handleChange('short_turn_fixed_surcharge_amount', updates.shortTurnFixedSurchargeAmount);
    if (updates.surchargeAppliesToShortedHoursOnly !== undefined) handleChange('surcharge_applies_to_shorted_hours_only', updates.surchargeAppliesToShortedHoursOnly);
    if (updates.premiumType !== undefined) handleChange('short_turn_premium_type', updates.premiumType);
    if (updates.premiumValue !== undefined) handleChange('short_turn_premium_value', updates.premiumValue);
    if (updates.premiumBasis !== undefined) handleChange('short_turn_premium_basis', updates.premiumBasis);
    if (updates.applyOnHalfDays !== undefined) handleSwitchChange('short_turn_apply_on_half_days', updates.applyOnHalfDays);
    if (updates.applicableRoleIds !== undefined) handleChange('short_turn_applicable_role_ids', updates.applicableRoleIds);
  };

  return (
    <ShortTurnConfigForm
      title={hideTitle ? "" : "Short Turn Surcharge"}
      description={hideTitle ? "" : "Override default short turn settings for this client."}
      usesShortTurn={uses_short_turn ?? false}
      onUsesShortTurnChange={(checked) => handleSwitchChange('uses_short_turn', checked)}
      roles={roles}
      shortTurnConfig={shortTurnConfig}
      onShortTurnConfigChange={onShortTurnConfigChange}
      rateMultipliers={rate_multipliers}
      hasAdditionalMultipliers={hasAdditionalMultipliers}
      minimumBillableHours={minimum_billable_hours}
      defaultBreakInputProps={{
        value: shortTurnConfig.requiredBreakHours ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onShortTurnConfigChange({ requiredBreakHours: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('required_break_hours'),
        disabled: isLoading,
        id: 'required_break_hours',
      }}
      defaultBreakError={getFieldErrorElement('required_break_hours', errors, touchedFields)}
      shortTurnFixedSurchargeAmountProps={{
        value: shortTurnConfig.shortTurnFixedSurchargeAmount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onShortTurnConfigChange({ shortTurnFixedSurchargeAmount: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('short_turn_fixed_surcharge_amount'),
        disabled: isLoading,
        id: 'short_turn_fixed_surcharge_amount',
      }}
      shortTurnFixedSurchargeAmountError={getFieldErrorElement('short_turn_fixed_surcharge_amount', errors, touchedFields)}
      shortTurnPremiumValueProps={{
        value: shortTurnConfig.premiumValue ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onShortTurnConfigChange({ premiumValue: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('short_turn_premium_value'),
        disabled: isLoading,
      }}
      shortTurnPremiumValueError={getFieldErrorElement('short_turn_premium_value', errors, touchedFields)}
      noCard={noCard}
      isCustomized={isCustomized}
      onRevert={hideRevert ? undefined : onRevert}
      customizedFields={shortTurnConfigCustomized}
      context="client"
      shortTurnApplicableRoleIds={shortTurnConfig.applicableRoleIds}
      onShortTurnApplicableRoleIdsChange={(roleIds) => onShortTurnConfigChange({ applicableRoleIds: roleIds })}
      hideToggle={hideToggle}
    />
  );
};
